import { PromptElementImplInterface } from './PromptElementImplInterface';
import { PromptElementController, DataStateChangeEvent, CheckBoxList } from '@sas-dvr/ltjs-bird';
import { ReportState } from '@sas-dvr/internal-va-react-core/store/rootReducer';
import { getFormattedData, getRequired } from './utils';
import { CheckboxListState } from '@sas-dvr/internal-va-react-core/store/birdElement/checkboxListState';
import {
  filter,
  tap,
  ignoreElements,
  map,
  mergeMap,
  distinctUntilChanged,
  skipWhile,
} from 'rxjs/operators';
import { Observable, EMPTY, of } from 'rxjs';
import { PortableEvent } from '@sas-dvr/ltjs-commons';
import { StateObservable } from 'redux-observable';
import { AnyAction } from 'redux';
import { Mutable } from '@sas-dvr/internal-va-react-core/utility-types';

export class CheckboxListImpl implements PromptElementImplInterface {
  private _controller: PromptElementController;
  private _id: string;
  private _searchable: boolean;
  private _selectAllEnabled: boolean;
  private _ignoreValueChange: boolean = false;

  constructor(controller: PromptElementController, id: string, specialization: CheckBoxList) {
    this._controller = controller;
    this._id = id;
    this._searchable = specialization.getSearchable();
    this._selectAllEnabled = specialization.getSelectAllEnabled();
  }

  createElement() {
    const indices = this._controller.getCurrentValuesInTermsOfOrderedUniqueRowIndices();

    const data = getFormattedData(this._controller);

    return CheckboxListState.actions.add(this._id, {
      name: data.name,
      valueColumnLabel: data.valueColumnLabel,
      measureColumnLabel: data.measureColumnLabel,
      required: getRequired(this._controller),
      values: data.values,
      measureValues: data.measureValues,
      selectedIndices: indices,
      searchable: this._searchable,
      selectAllEnabled: this._selectAllEnabled,
    });
  }

  deleteElement() {
    return CheckboxListState.actions.delete(this._id);
  }

  handleDataChange(dataChange$: Observable<DataStateChangeEvent>) {
    return dataChange$.pipe(
      map(() => {
        const indices = this._controller.getCurrentValuesInTermsOfOrderedUniqueRowIndices();
        const data = getFormattedData(this._controller);

        return CheckboxListState.actions.update(this._id, {
          valueColumnLabel: data.valueColumnLabel,
          measureColumnLabel: data.measureColumnLabel,
          values: data.values,
          measureValues: data.measureValues,
          selectedIndices: indices,
        });
      })
    );
  }

  handleValueChanged(valueChange$: Observable<PortableEvent>) {
    return valueChange$.pipe(
      mergeMap(() => {
        if (this._ignoreValueChange) {
          return EMPTY;
        }
        const indices = this._controller.getCurrentValuesInTermsOfOrderedUniqueRowIndices();
        return of(
          CheckboxListState.actions.update(this._id, {
            selectedIndices: indices,
          })
        );
      })
    );
  }

  handleActions(_myActions$: Observable<AnyAction>, state$: StateObservable<ReportState>) {
    return state$.pipe(
      map((aState) => aState.birdElements.CheckboxList[this._id]),
      filter((myState): myState is Exclude<typeof myState, undefined> => myState !== undefined),
      map((myState) => myState.selectedIndices as Mutable<typeof myState.selectedIndices>),
      distinctUntilChanged(),
      skipWhile((val) => val === null || val === undefined),
      tap((selectedIndices) => {
        this._ignoreValueChange = true;
        this._controller.setCurrentValuesInTermsOfOrderedUniqueRowIndices(selectedIndices ?? []);
        this._ignoreValueChange = false;
      }),
      ignoreElements()
    );
  }
}
